import { useCallback, useMemo, useState } from "react";
import { Resolver } from "../../../utils/Resolver/Resolver";

export type UseConfirmation<T> = {
  isOpen: boolean;
  onOpen(): Promise<T>;
  onClose(result?: T): void;
};

// TODO: Assess current usages of this hook and see if we can replace them with `useDisclosure` instead
/**
 * Creates an awaitable disclosure.
 *
 * # Most of the time you probably want `useDisclosure` instead. See {@link ConfirmationDialog} for examples.
 */
export function useConfirmation<T>(): UseConfirmation<T> {
  const [currentResolver, setResolver] = useState<Resolver<T> | null>(null);
  const isOpen = !!currentResolver;

  const onOpen = useCallback(() => {
    const resolver = new Resolver<T>();
    setResolver(resolver);
    return resolver.promise;
  }, [setResolver]);

  const onClose = useCallback(
    (result?: T) => {
      currentResolver?.resolve(result!);
      setResolver(null);
    },
    [currentResolver, setResolver],
  );

  return useMemo(
    () => ({
      isOpen,
      onOpen,
      onClose,
    }),
    [isOpen, onClose, onOpen],
  );
}
